/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Sortable Item
 */

import React from 'react';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, CheckboxChangeEvent, Flex, List, Button, Tag, Tooltip } from 'antd';
import { Drag, Pin, Pushpin } from '@icon-park/react';
import { useSortable } from '@dnd-kit/sortable';
import clsx from 'clsx';
import { isSpecialColumn } from './utils';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';
import { MAX_CUSTOM_FIELD_COLUMNS } from 'app-constants/admin/customAttribute';

const disabledColor = 'rgba(0, 0, 0, 0.25)';

/**
 * Pin Icon
 * @param {React.FC<{ disabled?: boolean }>} props
 * @returns {React.FC} PinIcon
 */
const PinIcon: React.FC<{ disabled?: boolean }> = ({ disabled, ...props }) => (
	<Pin theme='outline' fill={disabled ? disabledColor : '#333'} {...props} />
);

/**
 * UnPin Icon
 * @param {React.FC<{ disabled?: boolean }>} props
 * @returns {React.FC} UnPinIcon
 */
const UnPinIcon: React.FC<{ disabled?: boolean }> = ({ disabled, ...props }) => (
	<Pushpin theme='filled' fill={disabled ? disabledColor : '#333'} {...props} />
);

/**
 * Drag Handle
 * @param {React.FC<{ disabled?: boolean }>} props
 * @returns {React.FC} DragHandle
 */
const DragHandle: React.FC<{ disabled?: boolean }> = ({ disabled, ...props }) => (
	<Drag
		theme='outline'
		size='14'
		fill={disabled ? disabledColor : '#333'}
		style={{ cursor: disabled ? 'not-allowed' : 'grab' }}
		{...props}
	/>
);

interface IProps {
	value: IColumnMeta;
	isCustomFieldLimitReached?: boolean;
	onCheckboxChange: (event: CheckboxChangeEvent, colId: string) => void;
	onPinnedChange: (colId: string, pinned: 'left' | boolean) => void;
}

/**
 * Sortable Item
 * @param {IProps} props
 * @returns {React.FC} SortableItem
 */
const SortableItem: React.FC<IProps> = (props) => {
	const { value, isCustomFieldLimitReached, onCheckboxChange, onPinnedChange } = props;
	const colId = value.colId || '';
	const isSpecial = isSpecialColumn(colId);
	const isCustomField = value.isCustomField;
	const isDisabledByLimit = isCustomField && value?.hide && isCustomFieldLimitReached;
	const isDisabled = isSpecial || isDisabledByLimit;
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: colId, disabled: isDisabled });
	const style = { transform: CSS.Transform.toString(transform), transition, padding: 8 };
	const isPinned = value?.pinned === 'left' || value?.pinned === 'right';

	return (
		<List.Item ref={setNodeRef} style={style}>
			<Flex justify='space-between' style={{ width: '100%' }}>
				<Flex gap={10}>
					<div {...attributes} {...listeners}>
						<DragHandle disabled={isDisabled} />
					</div>
					<Tooltip
						title={isDisabledByLimit ? `Maximum ${MAX_CUSTOM_FIELD_COLUMNS} custom fields. Remove one to add another.` : ''}
					>
						<Checkbox checked={!value.hide} disabled={isDisabled} onChange={(event) => onCheckboxChange(event, colId)}>
							<Flex gap={8} align='center'>
								<div className={clsx('tw-ellipsis-text block max-w-60', isDisabled ? 'text-black/25' : 'text-initial')}>
									{value.title}
								</div>
								{isCustomField && (
									<Tag color={ANT_TAG_COLORS.magenta} className='text-xxs leading-4'>
										{'CA'}
									</Tag>
								)}
							</Flex>
						</Checkbox>
					</Tooltip>
				</Flex>
				<Button
					type='link'
					size='small'
					disabled={isDisabled}
					icon={isPinned ? <UnPinIcon disabled={isDisabled} /> : <PinIcon disabled={isDisabled} />}
					onClick={() => onPinnedChange(colId, isPinned ? false : 'left')}
				/>
			</Flex>
		</List.Item>
	);
};

export { DragHandle };
export default SortableItem;
